/**
 * The URL bridge for a guest's contact across the redirect-3DS round trip. The order
 * engine writes the live contact onto the Stripe return URL ({@link appendGuestContactParams});
 * on a failed return that routes to "Retry Payment" those params survive the error/retry URL
 * rebuild (they're not in the transient deny-list), and the route reads them back
 * ({@link guestContactFromParams}) to stamp the intent so the form re-opens pre-filled. The
 * order-detail API redacts buyer PII, so the URL is the only carrier. See `GuestContact`.
 */
import type { CheckoutParams } from '../types/CheckoutParams';
import type { GuestContact } from '../types/GuestContact';

/** The query keys carrying a guest's contact — one home so the read/write sides can't drift. */
export const GUEST_CONTACT_PARAM_KEYS = {
  email: 'guest_email',
  phone: 'guest_phone',
  phoneCountry: 'guest_phone_country',
  name: 'guest_name',
} as const;

/**
 * Read the guest contact a return / retry URL carries, or `null` when none is present
 * (a normal first-load checkout, or a signed-in buyer who entered none).
 */
export function guestContactFromParams(
  params: CheckoutParams,
): GuestContact | null {
  const email = params.guest_email ?? null;
  const phone = params.guest_phone ?? null;
  const phoneCountry = params.guest_phone_country ?? null;
  const name = params.guest_name ?? null;
  if (email == null && phone == null && name == null) {
    return null;
  }
  return { email, phone, phoneCountry, name };
}

/**
 * Append the guest's entered contact to a URL's params — only the fields actually set,
 * so a signed-in buyer (all null) adds nothing. Mutates `params` in place.
 */
export function appendGuestContactParams(
  params: URLSearchParams,
  contact: GuestContact,
): void {
  if (contact.email) {
    params.set(GUEST_CONTACT_PARAM_KEYS.email, contact.email);
  }
  if (contact.phone) {
    params.set(GUEST_CONTACT_PARAM_KEYS.phone, contact.phone);
    // Only meaningful alongside a phone — the region that makes those national digits
    // dialable. Without it a returning buyer's number is re-read against the default
    // country, silently changing which number they entered.
    if (contact.phoneCountry) {
      params.set(GUEST_CONTACT_PARAM_KEYS.phoneCountry, contact.phoneCountry);
    }
  }
  if (contact.name) {
    params.set(GUEST_CONTACT_PARAM_KEYS.name, contact.name);
  }
}
